import { Button } from '@/components/ui/button'
import { DownloadCloud02Icon, InfoCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/translations/i18n'

/**
 * "Download CSV" card: the live tally (ready / pending / unresolvable) and the
 * download action, disabled while anything still blocks the run.
 */
export function GenerateExportCard({
	included,
	pendingCount,
	excludedCount,
	gateBlocked,
	isExporting,
	onExport
}: {
	included: number
	pendingCount: number
	excludedCount: number
	gateBlocked: boolean
	isExporting: boolean
	onExport: () => void
}) {
	const { t } = useTranslation()
	// The tally states every non-empty lane, in the design's order; zero-count
	// segments are dropped rather than rendered as "0 pending".
	const tally = [
		t('marquee:export_count_ready', { count: included }),
		pendingCount > 0 &&
			t('marquee:export_count_pending', { count: pendingCount }),
		excludedCount > 0 &&
			t('marquee:export_count_unresolvable', { count: excludedCount })
	]
		.filter(Boolean)
		.join(' · ')

	const hint = (() => {
		if (pendingCount > 0) {
			return t('marquee:export_generate_unlocks')
		}
		if (gateBlocked) {
			return t('marquee:export_generate_blocked')
		}
		return t('marquee:export_generate_idempotent')
	})()

	return (
		<section className='flex flex-col gap-6 rounded-airbnb-2xl bg-grey-50 l:p-8'>
			<div className='flex flex-col gap-2'>
				<h3 className='font-semibold text-lg text-neutral-900'>
					{t('marquee:export_generate_title')}
				</h3>
				<p className='text-neutral-500 text-sm'>{tally}</p>
			</div>
			<div className='flex flex-col gap-3'>
				<Button
					aria-busy={isExporting}
					disabled={
						gateBlocked ||
						pendingCount > 0 ||
						included === 0 ||
						isExporting
					}
					onClick={onExport}
					size='md'
					type='button'
					variant='primary'
				>
					{isExporting ? (
						<Spinner aria-hidden size={16} />
					) : (
						<DownloadCloud02Icon size={16} />
					)}
					{t('marquee:export_download_csv', { count: included })}
				</Button>
				<p className='flex items-center gap-1.5 text-neutral-500 text-xs'>
					<InfoCircleIcon className='size-3.5 shrink-0' />
					{hint}
				</p>
			</div>
		</section>
	)
}
